import { NavBox } from "@/components/atom/box/nav/NavBox";
import UserInformBox from "@/components/molecule/userInformBox/UserInformBox";
import { UserUpdate } from "@/components/organism/form/UserUpdateForm";
import { COMMON_TEXT } from "@/constants/text";
import { useLocalStorage } from "@/context/LocalStorageProvider";
import { useModal } from "@/context/ModalStackManager";
import { useUserFetch } from "@/fetch/user/useUserFetch";
import { Aside, Box, Button, Flex, Select, Stack, Text } from "@mantine/core";
import { BarcodeInput } from "@/components/molecule/barcodeInput/BarcodeInput";
import {
  IconMapPin,
  IconPrinter,
  IconSpacingVertical,
  IconUser,
  IconCamera,
  IconBarcode,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BarcodeScanner } from "@/components/molecule/barcodeScanner/BarcodeScanner";

export const AppShellAside = () => {
  const token = localStorage.getItem("authToken");
  const { data: user } = useUserFetch({ token: token ?? "", enabled: !!token });
  const userData = user?.data;
  const {
    id,
    username, // 아이디
    name, // 사용자이름
    equipmentName,
    equipmentCode,
    fromLocationCode,
    fromLocationName,
    toLocationCode,
    toLocationName,
    printerCode,
  } = userData ?? {};

  const { openModal, closeModal } = useModal();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { language, setLanguage, setWorkId } = useLocalStorage();

  const extractWorkId = (input: string): string | null => {
    // Try full URL first
    try {
      const url = new URL(input);
      const q = url.searchParams.get("workId");
      if (q) return q;
      const segments = url.pathname.split("/").filter(Boolean);
      const workIdx = segments.findIndex((s) => s === "work");
      if (workIdx >= 0 && segments[workIdx + 1]) return segments[workIdx + 1];
      const lastNumeric = [...segments].reverse().find((s) => /^\d+$/.test(s));
      if (lastNumeric) return lastNumeric;
    } catch {
      // Not a full URL, treat as path or raw
      const path = input.split("?")[0];
      const segments = path.split("/").filter(Boolean);
      const workIdx = segments.findIndex((s) => s === "work");
      if (workIdx >= 0 && segments[workIdx + 1]) return segments[workIdx + 1];
      const lastNumeric = [...segments].reverse().find((s) => /^\d+$/.test(s));
      if (lastNumeric) return lastNumeric;
    }
    return null;
  };

  const handleLogout = () => {
    fetch(`${process.env.REACT_APP_BASE_URL}/auth/logout`, {
      method: "POST",
      body: JSON.stringify({ username: username }),
      headers: {
        "Content-Type": "application/json",
      },
    });
    navigate("/");
    localStorage.clear();
    window.location.reload();
  };

  const changeLanguage = (lang: "ko" | "en") => {
    i18n.changeLanguage(lang);
    setLanguage(lang);
  };

  const openUserUpdateModal = () => {
    openModal(
      <UserUpdate
        fromLocationCode={fromLocationCode}
        toLocationCode={toLocationCode}
        printerCode={printerCode}
        equipmentCode={equipmentCode}
        userId={id}
        userName={username}
        name={name}
      />,
      "",
      COMMON_TEXT.TITLE.USER,
    );
  };

  const openScannerInput = async () => {
    await openModal(
      <Stack spacing="md" p="md" style={{ minWidth: 400 }}>
        <Text size="sm">스캐너로 QR을 스캔해 주세요.</Text>
        <BarcodeInput
          placeholder="스캐너로 입력 후 Enter"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const value = (e.currentTarget as HTMLInputElement).value?.trim();
              const workId = value ? extractWorkId(value) : null;
              if (workId) {
                try {
                  setWorkId(workId);
                } catch {}
                closeModal();
                navigate(`/work/${workId}`);
              } else {
                window.alert("URL에서 workId를 찾을 수 없습니다.");
              }
            }
          }}
        />
        <Button variant="default" onClick={() => closeModal()}>
          닫기
        </Button>
      </Stack>,
      "",
      "스캐너 입력",
      true,
    );
  };

  const openScanModePicker = async () => {
    const result = await openModal(
      <Box>
        <Stack p="md" spacing="md" style={{ minWidth: 440 }}>
          <Button
            size="lg"
            rightIcon={<IconCamera size={20} />}
            onClick={() => closeModal("camera")}
          >
            카메라로 스캔
          </Button>
          <Button
            size="lg"
            variant="light"
            rightIcon={<IconBarcode size={20} />}
            onClick={() => closeModal("scanner")}
          >
            스캐너로 스캔
          </Button>
        </Stack>
      </Box>,
      "",
      "스캔 방식 선택",
    );

    if (result === "camera") {
      await openModal(
        <BarcodeScanner
          onDetected={async (code) => {
            try {
              if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(String(code));
              }
            } catch {}
            closeModal(code);
          }}
          onCancel={() => closeModal()}
        />,
        "",
        "작업지시서 스캔",
      );
    } else if (result === "scanner") {
      await openScannerInput();
    }
  };

  return (
    <Aside
      p="xs"
      hiddenBreakpoint="sm"
      w={200}
      width={{ sm: 200, lg: 300 }}
      bg={"#272727"}
    >
      <Aside.Section grow>
        <Flex gap={"md"} direction={"column"}>
          <UserInformBox
            title={COMMON_TEXT.TITLE.USER}
            value={name ?? "-"}
            size="xs"
            icon={<IconUser />}
            onClick={openUserUpdateModal} // 여기에 클릭 이벤트를 바인딩
          />
          <UserInformBox
            title={COMMON_TEXT.TITLE.EQUIPMENT}
            value={equipmentName ?? COMMON_TEXT.DATA_EMPTY.NO_EQUIPMENT}
            size="xs"
            icon={<IconSpacingVertical />}
            onClick={openUserUpdateModal} // 여기에 클릭 이벤트를 바인딩
          />
          <UserInformBox
            title={COMMON_TEXT.TITLE.FROM_LOCATION}
            value={fromLocationName ?? COMMON_TEXT.DATA_EMPTY.NO_LOCATION}
            size="xs"
            icon={<IconMapPin />}
            onClick={openUserUpdateModal} // 여기에 클릭 이벤트를 바인딩
          />
          <UserInformBox
            title={COMMON_TEXT.TITLE.TO_LOCATION}
            value={toLocationName ?? COMMON_TEXT.DATA_EMPTY.NO_LOCATION}
            size="xs"
            icon={<IconMapPin />}
            onClick={openUserUpdateModal} // 여기에 클릭 이벤트를 바인딩
          />
          <UserInformBox
            title={COMMON_TEXT.TITLE.PRINTER}
            value={printerCode ?? COMMON_TEXT.DATA_EMPTY.NO_PRINTER}
            size="xs"
            icon={<IconPrinter />}
            onClick={openUserUpdateModal} // 여기에 클릭 이벤트를 바인딩
          />
        </Flex>
      </Aside.Section>
      <Aside.Section>
        <Flex direction={"column"} gap={"xs"}>
          <NavBox onClick={openScanModePicker}>작업지시서 스캔</NavBox>
          <Select
            value={language}
            size="xs"
            data={[
              { value: "ko", label: COMMON_TEXT.LANGUAGE.KOREAN },
              { value: "en", label: COMMON_TEXT.LANGUAGE.ENGLISH },
            ]}
            onChange={(value: "en" | "ko") => changeLanguage(value ?? "")}
          />
          <NavBox onClick={handleLogout}>{COMMON_TEXT.BUTTON.LOGOUT}</NavBox>
        </Flex>
      </Aside.Section>
    </Aside>
  );
};
